// ShortcutCheatSheet — Phase 6h modal overlay triggered by `?`.
//
// Groups the bindings by where they apply (Standup vs. Thread vs. Universal)
// so the cheat sheet actually teaches the mental model instead of being a
// flat alphabetical dump. Escape or backdrop-click dismisses.
//
// Purely presentational — the parent (Standup) owns the open/close state.
import { useEffect } from 'react';

type Row = { keys: string[]; label: string };
type Group = { title: string; rows: Row[] };

const GROUPS: Group[] = [
  {
    title: 'On Standup',
    rows: [
      { keys: ['J'], label: 'Focus next card in the Pile' },
      { keys: ['K'], label: 'Focus previous card' },
      { keys: ['C'], label: 'Start a new conversation' },
      { keys: ['Enter'], label: 'Open the focused thread' },
      { keys: ['Space'], label: 'Toggle select / open thread' },
    ],
  },
  {
    title: 'In a thread (Command Center)',
    rows: [
      { keys: ['R'], label: 'Resolve' },
      { keys: ['A'], label: 'Awaiting reply' },
      { keys: ['O'], label: 'Reopen (open)' },
      { keys: ['M'], label: 'Mark as spam' },
      { keys: ['Z'], label: 'Snooze menu' },
    ],
  },
  {
    title: 'Anywhere',
    rows: [
      { keys: ['/'], label: 'Focus global search' },
      { keys: ['?'], label: 'Show this cheat sheet' },
      { keys: ['Esc'], label: 'Close modal / cancel' },
    ],
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ShortcutCheatSheet({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'hsl(var(--ink) / 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'hsl(var(--stone))',
          borderRadius: 14,
          padding: '28px 32px',
          maxWidth: 560,
          width: '100%',
          boxShadow: '0 20px 60px hsl(var(--ink) / 0.25)',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Keyboard shortcuts</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'hsl(var(--ink) / 0.6)',
              cursor: 'pointer',
              fontSize: 14,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >
            close
          </button>
        </div>

        {GROUPS.map((g) => (
          <div key={g.title} style={{ marginBottom: 20 }}>
            <div
              style={{
                textTransform: 'uppercase',
                fontSize: 11,
                letterSpacing: '0.08em',
                color: 'hsl(var(--ink) / 0.55)',
                marginBottom: 8,
                fontWeight: 600,
              }}
            >
              {g.title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {g.rows.map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 14,
                    padding: '4px 0',
                  }}
                >
                  <span style={{ color: 'hsl(var(--ink))' }}>{r.label}</span>
                  <span style={{ display: 'flex', gap: 4 }}>
                    {r.keys.map((k) => (
                      <kbd
                        key={k}
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 6,
                          border: '1px solid hsl(var(--ink) / 0.14)',
                          background: 'hsl(var(--ink) / 0.04)',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'hsl(var(--ink))',
                          minWidth: 22,
                          textAlign: 'center',
                        }}
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: 'hsl(var(--ink) / 0.5)',
            borderTop: '1px solid hsl(var(--ink) / 0.1)',
            paddingTop: 10,
          }}
        >
          Shortcuts are ignored while you're typing in an input or textarea.
        </div>
      </div>
    </div>
  );
}
